import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  IconPlus,
  IconRefresh,
  IconUpload,
  IconDownload,
  IconLoader,
  IconBrandTwitter,
  IconPhoto,
  IconStar,
  IconDotsVertical
} from '@tabler/icons-react';

import { useCreatorManagement } from './hooks/useCreatorManagement';
import CreatorAddWizard from './CreatorAddWizard';
import CreatorTable from './CreatorTable';
import CreatorIntelligenceSearch from '../CreatorIntelligence/CreatorIntelligenceSearch';
import CreatorFilters from './CreatorFilters';
import TelegramGroupMatcher from './TelegramGroupMatcher';
import ReviewModal from '../CreatorReview/ReviewModal';
import { Breadcrumb } from '../../ui/breadcrumb';
import { PageHeader, PageHeaderTitle, PageHeaderDescription, PageHeaderActions } from '../../ui/page-header';
import { Button } from '../../ui/button';
import { ConfirmDialog } from '../../ui/confirm-dialog';
import { Dialog, DialogContent } from '../../ui/dialog';
import { EmptyState } from '../../ui/empty-state';
import { Spinner } from '../../ui/spinner';
import { useAuth } from '../../../contexts/auth/AuthContext';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '../../ui/dropdown-menu';

const CreatorDetailPanel = React.lazy(() => import('./CreatorDetailPanel'));

function CreatorManagement() {
  const [isAddingCreator, setIsAddingCreator] = React.useState(false);
  const [notesEditing, setNotesEditing] = React.useState(false);
  const visibleCreators = React.useRef([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const creatorId = searchParams.get('creator');
  const detailOpenedFromList = React.useRef(false);
  const detailRequestVersion = React.useRef(0);
  const addWizardRef = React.useRef(null);
  const pageRef = React.useRef(null);
  const listScroll = React.useRef(null);
  const listFocus = React.useRef(null);
  const [detailLoadingId, setDetailLoadingId] = React.useState('');
  const [detailError, setDetailError] = React.useState('');
  const [detailRetry, setDetailRetry] = React.useState(0);
  const { hasPermission } = useAuth();
  const canManageCreators = hasPermission('manage_creators');
  const {
    // State
    filteredCreators,
    searchTerm, setSearchTerm,
    filterStatus, setFilterStatus,
    filterType, setFilterType,
    sortBy, setSortBy,
    selectedCreators,
    showAddWizard, setShowAddWizard,
    showDetailModal, detailCreator,
    showReviewModal,
    batchSyncStatus,
    isUploading,
    deleteConfirmState, setDeleteConfirmState,
    deleteAllConfirmOpen, setDeleteAllConfirmOpen,
    // Cache context
    cacheLoading, isInitialized, loadProgress, error, totalCreators,
    hasMoreCreators, getAllCreators, refreshCache,
    loadMoreCreators, fetchAndUpdateCreator,
    // Handlers
    handleSelectCreator,
    handleSelectAll,
    handleOpenReviewModal,
    handleCloseReviewModal,
    handleCreatorAdded,
    handleBatchFollowerSync,
    handleBulkProfileImageSync,
    handleViewDetails,
    handleCloseDetail,
    handleCreatorUpdate,
    handleDeleteCreator,
    confirmDelete,
    confirmDeleteAll,
    handleFileUpload,
    exportCreators,
    fileInputRef,
  } = useCreatorManagement();
  const hasLoadedCreators = loadProgress.loaded > 0;
  // Keep the same rows mounted while writing a note, even if background filtering changes.
  if (!notesEditing) visibleCreators.current = filteredCreators;
  const currentById = new Map(filteredCreators.map(creator => [creator._id, creator]));
  const tableCreators = notesEditing ? visibleCreators.current.map(creator => currentById.get(creator._id) || creator) : filteredCreators;

  React.useLayoutEffect(() => {
    const container = pageRef.current?.closest('main');
    if (!container) return;
    if (creatorId) container.scrollTop = 0;
    else if (listScroll.current !== null) { container.scrollTop = listScroll.current; listFocus.current?.focus({ preventScroll: true }); }
  }, [creatorId]);

  React.useEffect(() => {
    if (searchParams.get('action') !== 'add') return;

    if (canManageCreators) setShowAddWizard(true);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('action');
    setSearchParams(nextParams, { replace: true });
  }, [canManageCreators, searchParams, setSearchParams, setShowAddWizard]);

  React.useEffect(() => {
    const requestVersion = ++detailRequestVersion.current;
    if (!creatorId) {
      detailOpenedFromList.current = false;
      setDetailLoadingId('');
      setDetailError('');
      if (showDetailModal || detailCreator) handleCloseDetail();
      return undefined;
    }
    if (!isInitialized) return undefined;
    if (detailCreator?._id === creatorId && showDetailModal) {
      setDetailLoadingId('');
      setDetailError('');
      return undefined;
    }

    if (detailCreator?._id !== creatorId && showDetailModal) handleCloseDetail();

    const cachedCreator = getAllCreators().find(creator => creator._id === creatorId);
    if (cachedCreator) {
      setDetailLoadingId('');
      setDetailError('');
      handleViewDetails(cachedCreator);
      return undefined;
    }

    setDetailLoadingId(creatorId);
    setDetailError('');
    fetchAndUpdateCreator(creatorId)
      .then(creator => {
        if (detailRequestVersion.current !== requestVersion) return;
        setDetailLoadingId('');
        if (creator) handleViewDetails(creator);
        else setDetailError('Creator details were not found.');
      })
      .catch((error) => {
        if (detailRequestVersion.current !== requestVersion) return;
        setDetailLoadingId('');
        setDetailError(error?.response?.status === 404
          ? 'This creator could not be found.'
          : 'Creator details could not be loaded.');
      });
    return () => { detailRequestVersion.current += 1; };
  }, [creatorId, detailCreator, detailRetry, fetchAndUpdateCreator, getAllCreators, handleCloseDetail, handleViewDetails, isInitialized, showDetailModal]);

  const openCreatorDetails = React.useCallback((creator, event) => {
    event?.stopPropagation();
    listScroll.current = pageRef.current?.closest('main')?.scrollTop || 0;
    listFocus.current = event?.currentTarget || null;
    detailOpenedFromList.current = true;
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('creator', creator._id);
    setSearchParams(nextParams);
  }, [searchParams, setSearchParams]);

  const closeCreatorDetails = React.useCallback(() => {
    if (detailOpenedFromList.current) {
      navigate(-1);
      return;
    }
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('creator');
    setSearchParams(nextParams, { replace: true });
  }, [navigate, searchParams, setSearchParams]);

  const clearCreatorSelection = React.useCallback(() => {
    detailOpenedFromList.current = false;
    handleCloseDetail();
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('creator');
    setSearchParams(nextParams, { replace: true });
  }, [handleCloseDetail, searchParams, setSearchParams]);

  return (
    <div ref={pageRef} className="flex flex-col h-full bg-surface-0">
      <div className={creatorId ? 'hidden' : 'flex min-h-0 flex-1 flex-col'}>
      {/* Header */}
      <div className="px-3 pt-3 sm:px-6 sm:pt-6 pb-0">
        <Breadcrumb
          items={[
            { label: 'Home', href: '/' },
            { label: 'Creators' },
          ]}
          className="mb-3"
        />

        <PageHeader>
          <div>
            <PageHeaderTitle>Creators</PageHeaderTitle>
            <PageHeaderDescription>
              {filteredCreators.length} shown
              {totalCreators > loadProgress.loaded ? ` · ${loadProgress.loaded} loaded of ${totalCreators}` : ''}
              {selectedCreators.size > 0 && ` · ${selectedCreators.size} selected`}
            </PageHeaderDescription>
          </div>

          <fieldset disabled={notesEditing} className="min-w-0"><PageHeaderActions>
            {/* Review Button - hidden on mobile when no selection */}
            {canManageCreators && selectedCreators.size > 0 && (
              <Button
                variant="default"
                onClick={handleOpenReviewModal}
                size="sm"
                className="sm:size-auto"
              >
                <IconStar className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Review ({selectedCreators.size})</span>
                <span className="sm:hidden">{selectedCreators.size}</span>
              </Button>
            )}
            {/* Add Creator */}
            {canManageCreators && <Button variant="outline" className="min-h-11" onClick={() => navigate('/creator-notes')}>Notes focus</Button>}
            {canManageCreators && <TelegramGroupMatcher />}
            {canManageCreators && (
              <Button
                onClick={() => setShowAddWizard(true)}
                aria-label="Add creator"
                className="min-h-11"
              >
                <IconPlus className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Add Creator</span>
              </Button>
            )}

            {/* Mobile: overflow menu for sync/import/export */}
            {canManageCreators && <div className="sm:hidden">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" disabled={batchSyncStatus !== null} aria-label="More creator actions" className="min-h-11 min-w-11">
                    {batchSyncStatus ? (
                      <IconLoader className="h-4 w-4 animate-spin" />
                    ) : (
                      <IconDotsVertical className="h-4 w-4" />
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuItem onClick={handleBatchFollowerSync}>
                    <IconBrandTwitter className="h-4 w-4 mr-2 text-text-secondary" />
                    Sync Followers
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleBulkProfileImageSync}>
                    <IconPhoto className="h-4 w-4 mr-2 text-accent" />
                    Fill Missing Profile Images
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => fileInputRef.current?.click()}>
                    <IconUpload className="h-4 w-4 mr-2" />
                    Import CSV
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={exportCreators} disabled={!hasLoadedCreators}>
                    <IconDownload className="h-4 w-4 mr-2" />
                    Export loaded CSV
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>}

            {/* Desktop: separate sync dropdown + import/export buttons */}
            {canManageCreators && <div className="hidden sm:flex items-center gap-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" disabled={batchSyncStatus !== null}>
                    <IconRefresh className={`h-4 w-4 mr-2 ${batchSyncStatus ? 'animate-spin' : ''}`} />
                    Sync
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuItem onClick={handleBatchFollowerSync}>
                    <IconBrandTwitter className="h-4 w-4 mr-2 text-text-secondary" />
                    <div>
                      <p className="font-medium">Sync Followers</p>
                      <p className="text-xs text-text-tertiary">Update follower counts</p>
                    </div>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleBulkProfileImageSync}>
                    <IconPhoto className="h-4 w-4 mr-2 text-accent" />
                    <div>
                      <p className="font-medium">Fill Missing Profile Images</p>
                      <p className="text-xs text-text-tertiary">Fill missing profile pictures</p>
                    </div>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {batchSyncStatus ? (
                <Button variant="outline" disabled>
                  <IconLoader className="h-4 w-4 animate-spin mr-2" />
                  {batchSyncStatus.status === 'uploading'
                    ? 'Uploading...'
                    : batchSyncStatus.status === 'deleting'
                    ? 'Deleting...'
                    : `${batchSyncStatus.completedCreators || 0}/${batchSyncStatus.totalCreators || 0}`}
                </Button>
              ) : (
                <>
                  <Button variant="outline" onClick={() => fileInputRef.current?.click()}>
                    <IconUpload className="h-4 w-4 mr-2" />
                    Import
                  </Button>
                  <Button variant="outline" onClick={exportCreators} disabled={!hasLoadedCreators}>
                    <IconDownload className="h-4 w-4 mr-2" />
                    Export loaded
                  </Button>
                </>
              )}
            </div>}

            {!canManageCreators && (
              <Button variant="outline" onClick={exportCreators} disabled={!hasLoadedCreators} aria-label="Export loaded creators" className="min-h-11">
                <IconDownload className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Export loaded</span>
              </Button>
            )}

            {canManageCreators && <input
              ref={fileInputRef}
              id="file-upload"
              type="file"
              accept=".csv"
              className="hidden"
              onChange={handleFileUpload}
              disabled={isUploading}
            />}
          </PageHeaderActions></fieldset>
        </PageHeader>
      </div>

      {/* Status Banners */}
      {!isInitialized && cacheLoading && (
        <div className="mx-3 sm:mx-6 mt-4 p-3 bg-surface-2 border border-glass-border">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <IconLoader className="h-4 w-4 animate-spin text-text-secondary" />
              <span className="text-sm font-medium text-text-primary">Loading creators...</span>
            </div>
            <span className="text-sm text-text-tertiary">
              {loadProgress.loaded} of {loadProgress.total}
            </span>
          </div>
        </div>
      )}

      {error && (
        <div className="mx-3 sm:mx-6 mt-4 p-3 bg-surface-2 border border-glass-border">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-text-primary">
              Failed to load creators: {error}
            </span>
            <Button variant="destructive" size="sm" onClick={isInitialized ? loadMoreCreators : refreshCache}>
              Retry
            </Button>
          </div>
        </div>
      )}

      {isInitialized && hasMoreCreators && (
        <div role="status" className="mx-3 sm:mx-6 mt-4 p-3 bg-accent-muted border border-accent/20">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <IconLoader className={`h-4 w-4 text-accent${error ? '' : ' animate-spin'}`} />
              <span className="text-xs font-medium text-text-primary">{error ? 'Creator loading paused — retry to load the rest' : 'Loading all creators…'}</span>
            </div>
            <span className="text-xs text-text-tertiary">
              {loadProgress.loaded} / {loadProgress.total}
            </span>
          </div>
        </div>
      )}

      {/* Filters */}
      <fieldset disabled={notesEditing} className="px-3 sm:px-6 pt-4">
        <CreatorIntelligenceSearch />
        <CreatorFilters
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          filterStatus={filterStatus}
          onFilterStatusChange={setFilterStatus}
          filterType={filterType}
          onFilterTypeChange={setFilterType}
          sortBy={sortBy}
          onSortChange={setSortBy}
        />
      </fieldset>
      {notesEditing && <p role="status" className="px-3 py-2 text-sm text-text-secondary sm:px-6">Save or cancel the open note to change filters, sorting or pages.</p>}

      {/* Content Area */}
      <div className="flex-1 overflow-y-auto px-3 sm:px-6 pb-6">
        {!notesEditing && !isInitialized && cacheLoading ? (
          <div className="flex flex-col justify-center items-center py-16">
            <Spinner size="lg" className="mb-4" />
            <p className="text-sm text-text-tertiary">Loading creators...</p>
            <p className="text-xs text-text-tertiary mt-2">
              {loadProgress.loaded} of {loadProgress.total} loaded
            </p>
          </div>
        ) : !notesEditing && !isInitialized && error ? (
          <div className="py-12 text-center text-sm text-text-secondary">Creator list unavailable. Use Retry above to load it again.</div>
        ) : tableCreators.length === 0 ? (
          <EmptyState
            title="No creators found"
            description={searchTerm || !['all', '__all__'].includes(filterStatus) || !['all', '__all__'].includes(filterType) ? 'Try adjusting your filters' : 'Add your first creator to get started'}
            action={
              canManageCreators && !searchTerm && ['all', '__all__'].includes(filterStatus) && ['all', '__all__'].includes(filterType) && (
                <Button onClick={() => setShowAddWizard(true)}>
                  <IconPlus className="h-4 w-4 mr-2" />
                  Add Creator
                </Button>
              )
            }
          />
        ) : (
          <CreatorTable
            creators={tableCreators}
            onView={openCreatorDetails}
            onDelete={handleDeleteCreator}
            onUpdate={handleCreatorUpdate}
            onNotesEditingChange={setNotesEditing}
            onNotesFocus={creator => navigate(`/creator-notes?creator=${encodeURIComponent(creator._id)}`)}
            selectedCreators={selectedCreators}
            onSelectCreator={handleSelectCreator}
            onSelectAll={handleSelectAll}
            canManageCreators={canManageCreators}
          />
        )}
      </div>

      </div>
      {detailLoadingId === creatorId && (
        <div className="mx-3 mb-4 border border-glass-border bg-surface-1 p-3 text-sm text-text-secondary sm:mx-6" role="status">
          Loading selected creator…
        </div>
      )}
      {detailError && creatorId && (
        <div className="mx-3 mb-4 flex flex-wrap items-center justify-between gap-3 border border-error/40 bg-error-muted p-3 text-sm text-error sm:mx-6" role="alert">
          <span>{detailError}</span>
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="outline" className="min-h-11" onClick={() => setDetailRetry(value => value + 1)}>Retry creator details</Button>
            <Button type="button" variant="secondary" className="min-h-11" onClick={clearCreatorSelection}>Close creator details</Button>
          </div>
        </div>
      )}

      {/* Side Panel */}
      <React.Suspense fallback={null}>
        <CreatorDetailPanel
          open={showDetailModal && !!detailCreator && detailCreator._id === creatorId}
          onOpenChange={(open) => { if (!open) closeCreatorDetails(); }}
          creator={detailCreator}
          onUpdate={handleCreatorUpdate}
          readOnly={!canManageCreators}
        />
      </React.Suspense>

      <Dialog
        open={showAddWizard}
        onOpenChange={(open) => {
          if (!open) addWizardRef.current?.requestClose();
        }}
      >
        <DialogContent
          className="max-h-[90dvh] w-[calc(100%-1.5rem)] overflow-y-auto sm:max-w-lg max-sm:bottom-0 max-sm:left-0 max-sm:right-0 max-sm:top-auto max-sm:w-full max-sm:max-w-none max-sm:translate-x-0 max-sm:translate-y-0"
          onEscapeKeyDown={(event) => {
            if (isAddingCreator) event.preventDefault();
          }}
          onPointerDownOutside={(event) => {
            if (isAddingCreator) event.preventDefault();
          }}
          showCloseButton={!isAddingCreator}
          closeLabel="Close add creator"
        >
          {showAddWizard && (
            <CreatorAddWizard
              ref={addWizardRef}
              onCreatorAdded={handleCreatorAdded}
              onOpenNotes={creator => {
                setShowAddWizard(false);
                navigate(`/creator-notes?creator=${encodeURIComponent(creator._id)}`);
              }}
              onCancel={() => setShowAddWizard(false)}
              onSavingChange={setIsAddingCreator}
            />
          )}
        </DialogContent>
      </Dialog>

      {showReviewModal && (
        <ReviewModal
          open={showReviewModal}
          onClose={handleCloseReviewModal}
          creatorIds={Array.from(selectedCreators)}
        />
      )}

      {/* Confirm Dialogs */}
      <ConfirmDialog
        open={deleteConfirmState.open}
        onOpenChange={(open) => !open && setDeleteConfirmState({ open: false, creatorId: null })}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteConfirmState({ open: false, creatorId: null })}
        title="Delete Creator"
        description="Are you sure you want to delete this creator? This action cannot be undone."
        confirmText="Delete"
        variant="destructive"
      />

      <ConfirmDialog
        open={deleteAllConfirmOpen}
        onOpenChange={setDeleteAllConfirmOpen}
        onConfirm={confirmDeleteAll}
        onCancel={() => setDeleteAllConfirmOpen(false)}
        title="Delete All Creators"
        description="Are you sure you want to delete ALL creators? This action cannot be undone."
        confirmText="Delete All"
        variant="destructive"
      />
    </div>
  );
}

export default CreatorManagement;
